import {
  AppBar,
  Box,
  Button,
  Container,
  Stack,
  Toolbar,
  Typography,
  Tooltip,
  Badge,
} from "@mui/material";
import Logo from "../../../../assets/images/logo.svg";
import menu from "../../../../assets/images/menu.svg";
import { Link } from "react-router-dom";
import { Notifications } from "../../../notifications/Notifications";
import { Main } from "../Main";
import { handleRenderLink } from "../HandleLinks";
import { AuthLinks } from "./authLinks";
import { UserAvatar } from "./UserAvatar";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { getUser } from "../../../../redux/slices/AuthUser";

// Icons for logged-in icon bar
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import InventoryOutlinedIcon from "@mui/icons-material/InventoryOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import { useUnreadCount } from "../../../chat/hooks/useUnreadCount";

// Single icon-label nav item for the logged-in bar
const IconNavItem = ({ icon: Icon, label, to, badge }) => (
  <Tooltip title={label} arrow>
    <Button
      component={Link}
      to={to}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 0.15,
        minWidth: { xs: 36, sm: 56 },
        px: { xs: 0.4, sm: 1 },
        py: 0.5,
        color: "secondary.dark",
        borderRadius: "10px",
        textTransform: "none",
        transition: "all 200ms ease",
        "&:hover": {
          backgroundColor: "rgba(254, 31, 2, 0.06)",
          color: "primary.main",
        },
      }}
    >
      <Badge
        badgeContent={badge}
        color="error"
        sx={{ "& .MuiBadge-badge": { fontSize: 10, minWidth: 16, height: 16 } }}
      >
        <Icon sx={{ fontSize: { xs: 20, sm: 22 } }} />
      </Badge>
      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 600,
          lineHeight: 1.2,
          whiteSpace: "nowrap",
          display: { xs: "none", sm: "block" },
        }}
      >
        {label}
      </Typography>
    </Button>
  </Tooltip>
);

export const AppBars = ({
  user,
  handleOpenStack,
  handleOpenDrawer,
  navbarRef,
}) => {
  const { t } = useTranslation("header");
  const userId = useSelector(getUser)?.id;
  const unreadMessages = useUnreadCount();

  return (
    <AppBar
      position="sticky"
      sx={{
        backgroundColor: "rgba(251, 248, 245, 0.78)",
        zIndex: 1100,
        boxShadow: "0 14px 42px rgba(7, 31, 40, 0.07)",
        backdropFilter: "saturate(180%) blur(24px)",
        borderBottom: "1px solid rgba(7, 31, 40, 0.08)",
      }}
      ref={navbarRef}
    >
      <Main />
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: { xs: 68, md: 82 } }}>
          {/* ── Logo ──────────────────────────────────────────── */}
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              mr: 2,
              flexGrow: { xs: 1, sm: 0 },
              display: "flex",
              alignItems: "center",
              width: ["92px", "142px"],
              height: ["48px", "64px"],
              my: 0.75,
            }}
          >
            <img
              src={Logo}
              alt="DirectRent"
              loading="lazy"
              style={{ width: "100%", height: "100%", objectFit: "contain" }}
            />
          </Typography>

          {/* ── Desktop nav links (center) ─────────────────────── */}
          <Box
            sx={{
              display: ["none", "none", "none", "flex"],
              justifyContent: "center",
              alignItems: "center",
              flexGrow: 1,
            }}
          >
            {handleRenderLink(user).map(
              (link, index) =>
                link.renderd && (
                  <Button
                    key={index}
                    sx={{
                      color: "secondary.dark",
                      display: "block",
                      fontSize: "14px",
                      fontWeight: "bold",
                      mx: 0.25,
                      px: 1.8,
                      py: 0.9,
                      borderRadius: "999px",
                      transition:
                        "background-color 220ms ease, color 220ms ease, transform 220ms cubic-bezier(0.16, 1, 0.3, 1)",
                      "&:hover": {
                        backgroundColor: "rgba(254, 31, 2, 0.08)",
                        color: "primary.main",
                        transform: "translateY(-1px)",
                      },
                    }}
                    component={Link}
                    to={link.to}
                  >
                    {t(link.name)}
                  </Button>
                )
            )}
          </Box>

          {/* ── Right section ──────────────────────────────────── */}
          <Stack
            sx={{
              display: "flex",
              alignItems: "center",
              gap: { xs: "6px", lg: "4px" },
              justifyContent: "end",
              flexDirection: "row",
              flexGrow: [1, 1, 1, 0],
            }}
          >
            {user ? (
              <>
                {/* ── Icon bar ────────────────────────────────── */}
                <Stack
                  direction="row"
                  spacing={0}
                  alignItems="center"
                  sx={{ mr: 0.5 }}
                >
                  <Notifications />
                  <IconNavItem
                    icon={FavoriteBorderIcon}
                    label={t("favorites")}
                    to={`/favorite-products/${userId}`}
                  />
                  <IconNavItem
                    icon={ChatBubbleOutlineIcon}
                    label={t("chat")}
                    to="/chat"
                    badge={unreadMessages || undefined}
                  />
                  <IconNavItem
                    icon={InventoryOutlinedIcon}
                    label={t("my-products")}
                    to="/my-products"
                  />
                </Stack>

                {/* ── User avatar ──────────────────────────────── */}
                <UserAvatar
                  user={user}
                  handleOpenStack={handleOpenStack}
                />

                {/* ── Post Ad button ──────────────────────────── */}
                <Button
                  component={Link}
                  to="/add-new-product"
                  variant="contained"
                  startIcon={<AddCircleOutlineIcon sx={{ display: { xs: 'none', sm: 'block' } }} />}
                  sx={{
                    ml: 1,
                    px: { xs: 1.5, lg: 2.5 },
                    py: { xs: 0.7, lg: 1 },
                    borderRadius: "999px",
                    fontWeight: 700,
                    fontSize: { xs: 11, lg: 13 },
                    textTransform: "none",
                    backgroundColor: "primary.main",
                    color: "white",
                    boxShadow: "0 4px 14px rgba(254, 31, 2, 0.3)",
                    whiteSpace: "nowrap",
                    minWidth: 0,
                    "&:hover": {
                      backgroundColor: "#c91906",
                      boxShadow: "0 6px 20px rgba(254, 31, 2, 0.4)",
                      transform: "translateY(-1px)",
                    },
                    transition: "all 200ms ease",
                  }}
                >
                  {t("rent")}
                </Button>
              </>
            ) : (
              <AuthLinks />
            )}
          </Stack>

          {/* ── Mobile menu button ─────────────────────────────── */}
          <Box sx={{ display: ["block", "block", "block", "none"], ml: 2 }}>
            <img
              src={menu}
              alt="menu"
              style={{ width: "26px", height: "26px", cursor: "pointer" }}
              onClick={handleOpenDrawer}
            />
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
};
